import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useStore } from 'zustand';

import {
  IconRegularCart,
  IconRegularChevronRight,
  IconRegularPen,
  IconRegularShareIos,
  IconRegularTrashBin,
  theme,
} from '@notino/react-styleguide';

import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import { WISHLIST_BASE_URL } from '@constants';
import { SELECTION_MODE_HASH } from '@containers/WishList/constants';
import { useWishlistId } from '@containers/WishList/hooks/useWishlistId';
import { useWishlistStore } from '@containers/WishList/store';
import { styled } from '@linaria/react';

import { useCloseListMenuDrawer, useListMenuContext } from '../context';
import messages from '../messages';

import {
  ADD_ALL_TO_CART_MAX_WISHLIST_ITEMS,
  AddAllToCart,
} from './AddAllToCart/AddAllToCart';
import { Delete } from './Delete';
import { Rename } from './Rename';
import { Share } from './Share';
import { useShareList } from './useShareList';

const Wrapper = styled.div`
  padding: 0 0 20px;
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  padding: 1rem 2.25rem 1rem 1.25rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;

  // border substitution
  &:not(:last-child):after {
    content: '';
    height: 1px;
    background-color: ${theme.color.border.divider};
    position: absolute;
    bottom: -0.5px;
    left: 1.25rem;
    right: 1.25rem;
    z-index: 2;
  }

  svg {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }
  color: ${theme.color.text.primary};
  ${theme.typography.labelRegular400}

  &:hover {
    background-color: ${theme.color.background.primaryHover};
  }
`;

export const Content = () => {
  const { store } = useListMenuContext();
  const { list: _list, currentAction } = useStore(store);
  const list = _list!;
  const { share } = useShareList();
  const sharedListUrlRef = React.useRef<string>();

  const isDefaultList = list.id === useWishlistLists.DEFAULT_LIST_ID;
  const isEmpty = list.products.length === 0;
  const isWishlistDetailPage = Boolean(useWishlistId().listId);

  const handleShare = async () => {
    if (!sharedListUrlRef.current) {
      const { sharedListUrl } = await share(list.id, isDefaultList);
      sharedListUrlRef.current = sharedListUrl;
    }
    store.setState({ currentAction: 'share' });
    await navigator.share?.({ url: sharedListUrlRef.current });
  };

  if (currentAction === 'rename') {
    return <Rename />;
  }
  if (currentAction === 'delete') {
    return <Delete />;
  }
  if (currentAction === 'share') {
    return <Share sharedListUrl={sharedListUrlRef.current ?? ''} />;
  }
  if (currentAction === 'addToCart') {
    return <AddAllToCart />;
  }

  return (
    <Wrapper>
      {!list.isShared && !isDefaultList && (
        <Item onClick={() => store.setState({ currentAction: 'rename' })}>
          <IconRegularPen />
          <FormattedMessage {...messages.renameList} />
        </Item>
      )}
      {!list.isShared && (
        <>
          {isWishlistDetailPage && !isEmpty && (
            <ToggleSelectionModeItem>
              <IconRegularChevronRight />
              <FormattedMessage {...messages.moveOrDelete} />
            </ToggleSelectionModeItem>
          )}

          {!isWishlistDetailPage && !isEmpty && (
            <Item
              onClick={() =>
                window.location.assign(
                  `${WISHLIST_BASE_URL}/${list.id}${SELECTION_MODE_HASH}`
                )
              }
            >
              <IconRegularChevronRight />
              <FormattedMessage {...messages.moveOrDelete} />
            </Item>
          )}
        </>
      )}
      {list.products.length <= ADD_ALL_TO_CART_MAX_WISHLIST_ITEMS &&
        !isEmpty && (
          <Item onClick={() => store.setState({ currentAction: 'addToCart' })}>
            <IconRegularCart />
            <FormattedMessage {...messages.addAllToCard} />
          </Item>
        )}
      {!list.isShared && !isEmpty && (
        <Item onClick={handleShare}>
          <IconRegularShareIos />
          <FormattedMessage {...messages.share} />
        </Item>
      )}
      {!list.isShared && !isDefaultList && (
        <Item onClick={() => store.setState({ currentAction: 'delete' })}>
          <IconRegularTrashBin />
          <FormattedMessage {...messages.delete} />
        </Item>
      )}
    </Wrapper>
  );
};

const ToggleSelectionModeItem = ({ children }) => {
  const { toggleSelectionMode } = useWishlistStore((x) => x.actions);
  const close = useCloseListMenuDrawer();
  return (
    <Item
      onClick={() => {
        toggleSelectionMode();
        close();
      }}
    >
      {children}
    </Item>
  );
};
